import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import apiClient from '../../api/api';

const DEFAULT_RATE_KEY = 'STANDARD';

const formatNumber = (value) => {
  if (!Number.isFinite(value)) return '0';
  return Number(value).toLocaleString('es-ES');
};

const AdsFinanceEngine = ({ startDate, endDate, initialRateKey, onRateChange, onConfirm, previewEndpoint }) => {
  const [config, setConfig] = useState(null);
  const [configStatus, setConfigStatus] = useState('loading');
  const [rateKey, setRateKey] = useState(initialRateKey || DEFAULT_RATE_KEY);
  const [preview, setPreview] = useState(null);
  const [previewStatus, setPreviewStatus] = useState('idle');
  const [error, setError] = useState('');

  const isSelectionReady = Boolean(startDate && endDate && rateKey);
  const rateOptions = useMemo(() => {
    const entries = Object.entries(config?.rates || {});
    return entries.map(([key, value]) => ({ key, value }));
  }, [config]);

  useEffect(() => {
    let isMounted = true;
    const fetchConfig = async () => {
      setConfigStatus('loading');
      setError('');
      try {
        const response = await apiClient.get('/ads/finance/config');
        if (!isMounted) return;
        const nextConfig = response.data?.config;
        if (!nextConfig?.rates) {
          throw new Error('Config incompleta');
        }
        setConfig(nextConfig);
        setConfigStatus('ready');
        const defaultKey = nextConfig.defaultRateKey || DEFAULT_RATE_KEY;
        if (!rateKey) {
          setRateKey(defaultKey);
          if (onRateChange) onRateChange(defaultKey);
        }
      } catch (err) {
        if (!isMounted) return;
        setConfigStatus('error');
        setError('No se pudo cargar la configuracion financiera.');
      }
    };

    fetchConfig();
    return () => {
      isMounted = false;
    };
  }, [onRateChange, rateKey]);

  useEffect(() => {
    let timeoutId;
    let cancelled = false;

    if (!isSelectionReady) {
      setPreview(null);
      setPreviewStatus('idle');
      return undefined;
    }

    const runPreview = async () => {
      setPreviewStatus('loading');
      setError('');
      try {
        const response = await apiClient.post(previewEndpoint, {
          rateKey,
          startDate,
          endDate,
        });
        if (cancelled) return;
        const nextPreview = response.data?.preview;
        if (!nextPreview) {
          throw new Error('Preview incompleta');
        }
        setPreview(nextPreview);
        setPreviewStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setPreview(null);
        setPreviewStatus('error');
        setError(err.response?.data?.message || 'No se pudo validar el presupuesto.');
      }
    };

    timeoutId = setTimeout(runPreview, 350);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [endDate, rateKey, startDate, isSelectionReady, previewEndpoint]);

  const handleRateChange = (event) => {
    const nextKey = event.target.value;
    setRateKey(nextKey);
    if (onRateChange) onRateChange(nextKey);
  };

  const handleConfirm = () => {
    if (!preview?.allowed) return;
    if (onConfirm) onConfirm(preview);
  };

  const canConfirm = previewStatus === 'ready' && preview?.allowed;

  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h3 className="text-lg font-black text-slate-800">Motor financiero y de exposicion</h3>
          <p className="text-xs text-slate-500">SLA activo: {config?.slaWindow?.startHour ?? '--'}:00 - {config?.slaWindow?.endHour ?? '--'}:00 AST</p>
        </div>
        <div className="min-w-[220px]">
          <label className="text-xs uppercase text-slate-500">Tipo de tarifa</label>
          <select
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            value={rateKey}
            onChange={handleRateChange}
            disabled={configStatus !== 'ready'}
          >
            {rateOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.key} - {formatNumber(option.value)} Agoras/dia
              </option>
            ))}
          </select>
        </div>
      </div>

      {configStatus === 'loading' && (
        <div className="mt-4 text-sm text-slate-500">Cargando configuracion financiera...</div>
      )}
      {configStatus === 'error' && (
        <div className="mt-4 text-sm text-rose-600">{error}</div>
      )}

      {configStatus === 'ready' && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-xl bg-white border border-slate-200 p-3">
            <div className="text-xs uppercase text-slate-500">Costo total</div>
            <div className="text-lg font-black text-slate-800">
              {preview ? `${formatNumber(preview.totalCost)} Agoras` : '--'}
            </div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-3">
            <div className="text-xs uppercase text-slate-500">Impresiones estimadas</div>
            <div className="text-lg font-black text-slate-800">
              {preview ? formatNumber(preview.estimatedImpressions) : '--'}
            </div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-3">
            <div className="text-xs uppercase text-slate-500">Dias activos</div>
            <div className="text-lg font-black text-slate-800">
              {preview ? formatNumber(preview.durationDays) : '--'}
            </div>
          </div>
        </div>
      )}

      {previewStatus === 'loading' && (
        <div className="mt-3 text-xs text-slate-500">Validando saldo...</div>
      )}
      {previewStatus === 'error' && error && (
        <div className="mt-3 text-xs text-rose-600">{error}</div>
      )}

      {previewStatus === 'ready' && preview && (
        <div className="mt-3 text-xs text-slate-600">
          {preview.allowed
            ? `Tu campaña tendra una exposicion estimada de ${formatNumber(preview.estimatedImpressions)} impresiones en el periodo seleccionado.`
            : preview.message}
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm}
          className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          Confirmar presupuesto
        </button>
      </div>
    </section>
  );
};

AdsFinanceEngine.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  initialRateKey: PropTypes.string,
  onRateChange: PropTypes.func,
  onConfirm: PropTypes.func,
  previewEndpoint: PropTypes.string,
};

AdsFinanceEngine.defaultProps = {
  previewEndpoint: '/ads/finance/preview',
};

export default AdsFinanceEngine;
